/**
 * AvatarsCategory — picker screen for the avatar tier family (`/avatars`).
 *
 * The catalog collapses all avatar SKUs into a single "Avatars" category card
 * (see FeatureCard in Catalog.tsx) to avoid 6 tiers crowding the storefront.
 * Tapping that card lands here, where each LIVE avatar tier is a normal SkuCard
 * that routes onward to the existing /sku/:code detail + flow.
 *
 * Mounted OUTSIDE TabShell — uses MeSubpageShell for the back-chevron chrome.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../components/chrome/MeSubpageShell';
import { SkuCard } from '../components/catalog/SkuCard';
import { useSkus } from '../hooks/useSkus';
import type { Sku } from '../types/sku';
import cat from './Catalog.module.css';

/** Display order for avatar tiers — cheapest → premium. */
const AVATAR_ORDER = [
  'avatar_mini',
  'avatar_max',
  'zodiac_collection',
] as const;

export function AvatarsCategory(): JSX.Element {
  const navigate = useNavigate();
  const { data } = useSkus();
  const { t } = useTranslation('flows');

  const liveByCode = new Map<string, Sku>();
  for (const s of data ?? []) {
    if (s.status === 'LIVE') liveByCode.set(s.code, s);
  }

  // Ordered known tiers first, then any other live AVATAR SKUs not in the list.
  const ordered = AVATAR_ORDER.map((c) => liveByCode.get(c)).filter(Boolean) as Sku[];
  const extras = (data ?? []).filter(
    (s) => s.status === 'LIVE' && s.category === 'AVATAR' && !AVATAR_ORDER.includes(s.code as (typeof AVATAR_ORDER)[number]),
  );
  const avatars = [...ordered, ...extras];

  return (
    <MeSubpageShell title={t('catalog.avatarsScreen.title')} data-testid="avatars-category">
      <p className={cat.titleLine3} style={{ marginBottom: 20 }}>
        {t('catalog.avatarsScreen.subtitle')}
      </p>
      <div className={cat.grid}>
        {avatars.map((sku) => (
          <SkuCard key={sku.code} sku={sku} onTap={(s) => void navigate(`/sku/${s.code}`)} />
        ))}
      </div>
    </MeSubpageShell>
  );
}
